import React, { useState, useEffect } from "react";
import { CircleStackIcon } from "@heroicons/react/24/solid";
import { useCredits } from "../../contexts/CreditsContext";
import PaymentButton from "../../components/Payment/PaymentButton";
import { getCloudFunctionUrl } from "../../config/firebaseFunctionUrl";

export default function CreditsPage() {
  const { credits } = useCredits();
  const [creditPackages, setCreditPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const url = getCloudFunctionUrl('getStripeProducts');
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.products) {
          // Map the response to the format we need
          const packages = data.products.map(product => ({
            credits: product.credits,
            aiContent: product.aiContent,
            price: product.price,
            priceId: product.productId,
          }));
          setCreditPackages(packages);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching Stripe products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Credits</h1>

      {/* Current Balance */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your balance</h2>
        <div className="flex items-center text-3xl font-bold text-gray-900">
          <CircleStackIcon className="h-8 w-8 text-indigo-500 mr-2" />
          <span>{credits}</span>
        </div>
      </div>

      {/* Buy Credits */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Buy more credits
        </h2>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">Error loading credit packages: {error}</p>
          </div>
        )}
        
        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-gray-100 border border-gray-200 rounded-lg p-6 shadow-sm animate-pulse"
              >
                <div className="h-8 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-10 bg-gray-300 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              {creditPackages.map((pkg) => (
                <div
                  key={pkg.credits}
                  className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-center mb-4">
                    <CircleStackIcon className="h-6 w-6 text-indigo-500 mr-2" />
                    <span className="text-2xl font-bold text-gray-900">
                      {pkg.credits}
                    </span>
                  </div>
                  <PaymentButton
                    amount={pkg.price}
                    priceId={pkg.priceId}
                    credits={pkg.credits}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
